import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

const api = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 (unauthenticated) and 403 access_denied (blocked account)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    if (
      err.response?.status === 403 &&
      err.response?.data?.error === 'access_denied'
    ) {
      localStorage.setItem('blocked_message', err.response.data.message || '');
      window.location.href = '/blocked';
    }
    return Promise.reject(err);
  }
);

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
};

export const buildings = {
  list: () => api.get('/buildings'),
  create: (data) => api.post('/buildings', data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  delete: (id) => api.delete(`/buildings/${id}`),
};

export const contractors = {
  list: (params) => api.get('/contractors', { params }),
  create: (data) => api.post('/contractors', data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  rate: (id, rating) => api.post(`/contractors/${id}/rate`, { rating }),
  delete: (id) => api.delete(`/contractors/${id}`),
  specialties: () => api.get('/contractors/specialties'),
};

export const workorders = {
  list: (params) => api.get('/workorders', { params }),
  create: (data) => api.post('/workorders', data),
  update: (id, data) => api.put(`/workorders/${id}`, data),
  delete: (id) => api.delete(`/workorders/${id}`),
  stats: () => api.get('/workorders/stats'),
  autopilot: (data) => api.post('/workorders/autopilot', data),
};

export const documents = {
  list: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
  search: (query, buildingId) => api.post('/documents/search', { query, building_id: buildingId }),
  setOwnerVisible: (id, visible) => api.patch(`/documents/${id}`, { owner_visible: visible }),
  portfolioInsights: () => api.get('/documents/portfolio-insights'),
  downloadCount: (id) => api.get(`/documents/${id}/downloads`),
};

export const certificates = {
  previewData: (unitId) => api.get('/certificates/preview-data', { params: { unit_id: unitId } }),
  generate: (data) => api.post('/certificates/generate', data),
};

export const documentRequests = {
  list: (params) => api.get('/document-requests', { params }),
  fulfill: (id, data) => api.post(`/document-requests/${id}/fulfill`, data),
  decline: (id, reason) => api.post(`/document-requests/${id}/decline`, { reason }),
};

// Public document-request portal — bare axios instance, no auth token attached
const publicApi = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});

export const publicDocumentRequests = {
  searchBuildings: (q) => publicApi.get('/document-requests/buildings-search', { params: { q } }),
  searchUnits: (buildingId) => publicApi.get('/document-requests/units-search', { params: { building_id: buildingId } }),
  create: (data) => publicApi.post('/document-requests', data),
  checkout: (id, accessToken) => publicApi.post(`/document-requests/${id}/checkout`, { access_token: accessToken }),
  status: (token) => publicApi.get(`/document-requests/status/${token}`),
  downloadUrl: (token) => `${BASE}/document-requests/${token}/download`,
};

export const email = {
  compose: (data) => api.post('/email/compose', data),
  send: (data) => api.post('/email/send', data),
  templates: () => api.get('/email/templates'),
  createTemplate: (data) => api.post('/email/templates', data),
};

export const meetings = {
  list:            (params) => api.get('/meetings', { params }),
  create:          (data)   => api.post('/meetings', data),
  get:             (id)     => api.get(`/meetings/${id}`),
  update:          (id, d)  => api.put(`/meetings/${id}`, d),
  delete:          (id)     => api.delete(`/meetings/${id}`),
  uploadAudio:     (id, fd) => api.post(`/meetings/${id}/upload-audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  transcribe:      (id)     => api.post(`/meetings/${id}/transcribe`),
  generateMinutes: (id, d)  => api.post(`/meetings/${id}/generate-minutes`, d),
  suggestAgenda:   (id)     => api.post(`/meetings/${id}/suggest-agenda`),
};

export const invoices = {
  list:   (params) => api.get('/invoices', { params }),
  upload: (fd)     => api.post('/invoices/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  create: (data)   => api.post('/invoices', data),
  update: (id, d)  => api.put(`/invoices/${id}`, d),
  status: (id, s)  => api.post(`/invoices/${id}/status`, { status: s }),
  delete: (id)     => api.delete(`/invoices/${id}`),
  stats:  ()       => api.get('/invoices/stats'),
};

export const compliance = {
  checkText: (text, doc_type) => api.post('/compliance/check', { text, doc_type }),
  checkFile: (fd)             => api.post('/compliance/check', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const voiceWorkorder = {
  parse: (transcript) => api.post('/voice-workorder/parse', { transcript }),
};

// ─── Owner Portal ─────────────────────────────────────────────────────────────
// Separate axios instance that uses the owner JWT (stored under 'owner_token')
const ownerApi = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});

ownerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('owner_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

ownerApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('owner_token');
      window.location.href = '/owner/login';
    }
    return Promise.reject(err);
  }
);

export const ownerAuth = {
  register: (data) => ownerApi.post('/owner-auth/register', data),
  login: (data) => ownerApi.post('/owner-auth/login', data),
  me: () => ownerApi.get('/owner-auth/me'),
};

export const ownerPortal = {
  dashboard: () => ownerApi.get('/owner/dashboard'),
  fees: (params) => ownerApi.get('/owner/fees', { params }),
  maintenance: () => ownerApi.get('/owner/maintenance'),
  submitMaintenance: (fd, isFormData = false) =>
    ownerApi.post('/owner/maintenance', fd,
      isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
  documents: () => ownerApi.get('/owner/documents'),
  downloadDocument: (id) => ownerApi.get(`/owner/documents/${id}/download`, { responseType: 'blob' }),
  chat: (data) => ownerApi.post('/owner/chat', data),
  analyzePhoto: (file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return ownerApi.post('/owner/analyze-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const ownerPayments = {
  config: () => ownerApi.get('/payments/config'),
  createIntent: (data) => ownerApi.post('/payments/create-intent', data),
  confirm: (data) => ownerApi.post('/payments/confirm', data),
  history: () => ownerApi.get('/payments/history'),
};

export const units = {
  list: (params) => api.get('/units', { params }),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
  fees: (id) => api.get(`/units/${id}/fees`),
  generateFees: (id, data) => api.post(`/units/${id}/fees/generate`, data),
  stats: (params) => api.get('/units/stats', { params }),
  maintenance: (params) => api.get('/units/maintenance', { params }),
  updateMaintenance: (id, data) => api.put(`/units/maintenance/${id}`, data),
};

export const admin = {
  stats:              ()         => api.get('/admin/stats'),
  users:              ()         => api.get('/admin/users'),
  getUser:            (id)       => api.get(`/admin/users/${id}`),
  createUser:         (data)     => api.post('/admin/users', data),
  updateUser:         (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser:         (id)       => api.delete(`/admin/users/${id}`),
  sendTrialReminders: ()         => api.post('/admin/send-trial-reminders'),
};

export const stripe = {
  createCheckoutSession: (data) => api.post('/create-checkout-session', data),
};

export const predictions = {
  list:     (params)     => api.get('/predictions', { params }),
  insights: ()           => api.get('/predictions/insights'),
  analyze:  (force)      => api.post('/predictions/analyze', { force: !!force }),
  schedule: (id, data)   => api.post(`/predictions/${id}/schedule`, data || {}),
  dismiss:  (id)         => api.post(`/predictions/${id}/dismiss`),
  feedback: (id, accurate) => api.post(`/predictions/${id}/feedback`, { was_accurate: accurate }),
};

export const emailInbound = {
  address:  ()           => api.get('/email-inbound/address'),
  logs:     (params)     => api.get('/email-inbound/logs', { params }),
  simulate: (data)       => api.post('/email-inbound/simulate', data),
};

export const financials = {
  summary: (params) => api.get('/financials/summary', { params }),
  budget:  (params) => api.get('/financials/budget', { params }),
  saveBudget: (data) => api.post('/financials/budget', data),
  exportUrl: (params) => {
    const token = localStorage.getItem('token');
    const qs = new URLSearchParams({ ...params, _t: token }).toString();
    return `/api/financials/export?${qs}`;
  },
};

export const arrears = {
  list:          (params) => api.get('/arrears/', { params }),
  sendReminder:  (data)   => api.post('/arrears/send-reminder', data),
  sendBulk:      (data)   => api.post('/arrears/send-bulk-reminders', data),
};

export const bulkEmail = {
  send: (data) => api.post('/email/bulk-notice', data),
};

export const glCodes = {
  list: () => api.get('/gl-codes'),
};

// ─── Council Portal ────────────────────────────────────────────────────────────
const councilApi = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});

councilApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('council_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

councilApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('council_token');
      window.location.href = '/council/login';
    }
    return Promise.reject(err);
  }
);

export const councilAuth = {
  login: (data) => councilApi.post('/council-auth/login', data),
  me:    ()     => councilApi.get('/council-auth/me'),
};

export const councilPortal = {
  dashboard:  () => councilApi.get('/council/dashboard'),
  meetings:   () => councilApi.get('/council/meetings'),
  workorders: () => councilApi.get('/council/workorders'),
  financials: () => councilApi.get('/council/financials'),
};

export default api;
